// src/pages/AccountChecker.jsx
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import PageTransition from '../components/Shared/PageTransition';
import AccountCheckerHero from '../components/AccountChecker/AccountCheckerHero';
import AccountSearchHeroBox from '../components/AccountChecker/AccountSearchHeroBox';
import CredentialStuffingStatus from '../components/AccountChecker/CredentialStuffingStatus';
import DatabaseStats from '../components/AccountChecker/DatabaseStats';
import SearchOptions from '../components/AccountChecker/SearchOptions';
import AccountResults, { isHashValue } from '../components/AccountChecker/AccountResults';
import HowItWorks from '../components/AccountChecker/HowItWorks';

import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNotifications } from '../contexts/NotificationContext';
import { WS_ACCOUNT_CHECKER_TYPES, WS_ACCOUNT_CHECKER_TOPICS } from '../config/wsAccountCheckerConfig';
import TermsOfServiceModal from '../components/Shared/TermsOfServiceModal';
import { handleWebSocketError } from '../utils/websocketErrorHandler';

import './AccountCheckerPage.css';

const LOCAL_STORAGE_RECENT_SEARCHES_KEY = 'stormhack_recent_searches_cache';

const getInitialRecentSearches = () => {
  try {
    const cached = localStorage.getItem(LOCAL_STORAGE_RECENT_SEARCHES_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (e) {
    console.error('Failed to load recent searches from cache:', e);
  }
  return [];
};

const saveRecentSearchesToCache = (data) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_RECENT_SEARCHES_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save recent searches to cache:', e);
  }
};

const AccountChecker = ({ openModal }) => {
  const { language, t } = useLanguage();
  const isEn = language === 'en';

  const { isAuthenticated, triggerGoogleLogin } = useAuth();
  const { isConnected, sendMessage, subscribe } = useWebSocket();
  const { addNotification, showToast } = useNotifications();

  // Search input & current target (Starts empty, driven strictly by user input & backend)
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTargetUser, setActiveTargetUser] = useState('');
  const [rawResults, setRawResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [recentSearches, setRecentSearches] = useState(getInitialRecentSearches);

  // Search options state
  const [searchOptions, setSearchOptions] = useState({
    database: 'all', // 'all' | 'recent' | 'verified'
    searchType: 'exact', // 'exact' | 'fuzzy'
    maxResults: 25, // 10 | 25 | 50
  });

  // Global & Bot stats
  const [stats, setStats] = useState({
    totalAccounts: '60+M',
    uniquePasswords: '40+M',
    serverSources: '2,787',
    successRate: '69%',
  });

  const [botStats, setBotStats] = useState({
    botsActive: 600,
    successRate: 6,
    recentFinds: 47,
    serversMonitored: 1253,
    activeProxies: 92,
    lastUpdate: 'Just now'
  });

  // Smooth jitter for bot stats
  useEffect(() => {
    const interval = setInterval(() => {
      setBotStats(prev => {
        const offset = Math.floor(Math.random() * 5) - 2;
        return {
          ...prev,
          botsActive: Math.max(580, 600 + offset),
        };
      });
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // --- Real-time Recent Searches (Global across all users) ---
  const publicSubsRef = useRef({ broadcast: null, userRecent: null });

  const updateRecentSearchesList = useCallback((newItem) => {
    if (!newItem) return;
    const name = (newItem.username || newItem.name || '').trim();
    if (!name) return;

    setRecentSearches(prev => {
      const lowerName = name.toLowerCase();
      // Remove any existing entry with the same name (case-insensitive deduplication)
      const filtered = (prev || []).filter(item => {
        const existingName = (item.username || item.name || '').trim().toLowerCase();
        return existingName !== lowerName;
      });
      // Prepend the new/updated item to the top
      const updated = [newItem, ...filtered].slice(0, 10);
      saveRecentSearchesToCache(updated);
      return updated;
    });
  }, []);

  const handleRecentSearchesMessage = useCallback((message, wsError) => {
    if (wsError || !message?.data) return;
    const list = message.data.history || (Array.isArray(message.data) ? message.data : null);
    if (list && Array.isArray(list)) {
      setRecentSearches(prev => {
        const mergedMap = new Map();
        list.forEach(item => {
          const n = (item.username || item.name || '').trim();
          if (n) {
            const key = n.toLowerCase();
            if (!mergedMap.has(key)) {
              mergedMap.set(key, item);
            }
          }
        });
        (prev || []).forEach(item => {
          const n = (item.username || item.name || '').trim();
          if (n) {
            const key = n.toLowerCase();
            if (!mergedMap.has(key)) {
              mergedMap.set(key, item);
            }
          }
        });
        const updated = Array.from(mergedMap.values()).slice(0, 10);
        saveRecentSearchesToCache(updated);
        return updated;
      });
    } else if (message.data.username || message.data.name) {
      updateRecentSearchesList(message.data);
    }
  }, [updateRecentSearchesList]);

  const handleNewSearchBroadcast = useCallback((message) => {
    const item = message?.data && !message.data.history ? message.data : (message?.username || message?.name ? message : null);
    if (item && (item.username || item.name)) {
      updateRecentSearchesList(item);
    }
  }, [updateRecentSearchesList]);

  // Subscribe to public recent searches & get initial batch (works for both guests & logged in users)
  useEffect(() => {
    if (!isConnected) {
      Object.values(publicSubsRef.current).forEach(unsub => unsub?.());
      publicSubsRef.current = { broadcast: null, userRecent: null };
      return;
    }

    publicSubsRef.current.broadcast = subscribe(
      `${WS_ACCOUNT_CHECKER_TOPICS.PUBLIC_TOPIC_BASE}${WS_ACCOUNT_CHECKER_TOPICS.PUBLIC_RECENT_SEARCHES}`,
      handleNewSearchBroadcast
    );

    publicSubsRef.current.userRecent = subscribe(
      `${WS_ACCOUNT_CHECKER_TOPICS.USER_QUEUE_BASE}${WS_ACCOUNT_CHECKER_TOPICS.USER_RECENT_SEARCHES}`,
      handleRecentSearchesMessage
    );

    const timer = setTimeout(() => {
      try {
        sendMessage(`${WS_ACCOUNT_CHECKER_TOPICS.APP_ENDPOINT}${WS_ACCOUNT_CHECKER_TOPICS.APP_GET_RECENT_SEARCHES}`, {});
      } catch (e) {
        console.error('Failed to request recent searches:', e);
      }
    }, 120);

    return () => {
      clearTimeout(timer);
      Object.values(publicSubsRef.current).forEach(unsub => unsub?.());
      publicSubsRef.current = { broadcast: null, userRecent: null };
    };
  }, [isConnected, subscribe, sendMessage, handleNewSearchBroadcast, handleRecentSearchesMessage]);

  // --- Authenticated User Subscriptions (Check Results, Progress, Personal History) ---
  const subscriptionsRef = useRef({ results: null, errors: null, history: null });

  const handleResultsMessage = useCallback((message, wsError) => {
    if (wsError) {
      setIsLoading(false);
      handleWebSocketError(
        message, wsError, openModal, addNotification,
        { defaultTitle: t('accountCheckerError'), moduleName: t('accountCheckerModuleName') }
      );
      return;
    }

    switch (message?.type) {
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_INITIATED:
        setIsLoading(true);
        setRawResults([]);
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_PROGRESS:
        setIsLoading(true);
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_RESULT:
        if (message.data) {
          const item = {
            name: message.data.username || activeTargetUser,
            source: message.data.source || 'Database Match',
            date: message.data.lastVerified || new Date().toISOString().split('T')[0],
            pass: message.data.password || '',
            isHash: isHashValue(message.data.password)
          };
          const itemKey = `${(item.name || '').toLowerCase()}|${item.pass}|${item.date}`;
          setRawResults(prev => {
            if (prev.some(p => `${(p.name || '').toLowerCase()}|${p.pass}|${p.date}` === itemKey)) {
              return prev;
            }
            return [...prev, item];
          });
        }
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_BATCH_RESULT:
        const batch = Array.isArray(message.data)
          ? message.data
          : (Array.isArray(message.results) ? message.results : (message.data?.results || []));
        if (batch && Array.isArray(batch) && batch.length > 0) {
          const items = batch.map(r => ({
            name: r.username || activeTargetUser,
            source: r.source || 'Database Match',
            date: r.lastVerified || new Date().toISOString().split('T')[0],
            pass: r.password || '',
            isHash: isHashValue(r.password)
          }));
          setRawResults(prev => {
            const existingKeys = new Set(prev.map(p => `${(p.name || '').toLowerCase()}|${p.pass}|${p.date}`));
            const uniqueNew = items.filter(it => {
              const k = `${(it.name || '').toLowerCase()}|${it.pass}|${it.date}`;
              if (existingKeys.has(k)) return false;
              existingKeys.add(k);
              return true;
            });
            return [...prev, ...uniqueNew];
          });
        }
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_NO_RESULTS:
        setRawResults([]);
        setIsLoading(false);
        break;
      case WS_ACCOUNT_CHECKER_TYPES.CHECK_COMPLETED:
        setIsLoading(false);
        if (isConnected && isAuthenticated) {
          sendMessage(`${WS_ACCOUNT_CHECKER_TOPICS.APP_ENDPOINT}${WS_ACCOUNT_CHECKER_TOPICS.APP_GET_HISTORY}`, {});
        }
        window.dispatchEvent(new CustomEvent('showAiInvite', {
          detail: { message: t('aiAssistantInviteAccounts') }
        }));
        break;
      default:
        break;
    }
  }, [activeTargetUser, openModal, addNotification, t, isConnected, isAuthenticated, sendMessage]);

  const handleErrorMessage = useCallback((errorPayload, wsError) => {
    handleWebSocketError(
      errorPayload, wsError, openModal, addNotification,
      { defaultTitle: t('accountCheckerError'), moduleName: t('accountCheckerModuleName') }
    );
    setIsLoading(false);
  }, [openModal, addNotification, t]);

  const handleHistoryMessage = useCallback((message, wsError) => {
    if (wsError || message?.type === WS_ACCOUNT_CHECKER_TYPES.HISTORY_ERROR) {
      setSearchHistory([]);
    } else if (message?.type === WS_ACCOUNT_CHECKER_TYPES.HISTORY_LIST && message.data?.history) {
      setSearchHistory(message.data.history);
    }
  }, []);

  useEffect(() => {
    if (!isConnected || !isAuthenticated) {
      Object.values(subscriptionsRef.current).forEach(unsub => unsub?.());
      subscriptionsRef.current = { results: null, errors: null, history: null };
      return;
    }

    if (!subscriptionsRef.current.results) {
      subscriptionsRef.current.results = subscribe(
        `${WS_ACCOUNT_CHECKER_TOPICS.USER_QUEUE_BASE}${WS_ACCOUNT_CHECKER_TOPICS.RESULTS}`,
        handleResultsMessage
      );
    }
    if (!subscriptionsRef.current.errors) {
      subscriptionsRef.current.errors = subscribe(
        `${WS_ACCOUNT_CHECKER_TOPICS.USER_QUEUE_BASE}${WS_ACCOUNT_CHECKER_TOPICS.ERRORS}`,
        handleErrorMessage
      );
    }
    if (!subscriptionsRef.current.history) {
      subscriptionsRef.current.history = subscribe(
        `${WS_ACCOUNT_CHECKER_TOPICS.USER_QUEUE_BASE}${WS_ACCOUNT_CHECKER_TOPICS.USER_HISTORY}`,
        handleHistoryMessage
      );
    }

    sendMessage(`${WS_ACCOUNT_CHECKER_TOPICS.APP_ENDPOINT}${WS_ACCOUNT_CHECKER_TOPICS.APP_GET_HISTORY}`, {});

    return () => {
      Object.values(subscriptionsRef.current).forEach(unsub => unsub?.());
      subscriptionsRef.current = { results: null, errors: null, history: null };
    };
  }, [isConnected, isAuthenticated, subscribe, sendMessage, handleResultsMessage, handleErrorMessage, handleHistoryMessage]);

  // Terms of Service Modal State
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [pendingTargetUser, setPendingTargetUser] = useState(null);
  const hasAcceptedTermsRef = useRef(false);

  // Real Search Execution
  const executeSearch = useCallback((nick) => {
    setActiveTargetUser(nick);
    setHasSearched(true);
    setIsLoading(true);
    setRawResults([]);

    sendMessage(`${WS_ACCOUNT_CHECKER_TOPICS.APP_ENDPOINT}/start`, {
      username: nick,
      options: searchOptions
    });

    // Safety timeout guard
    setTimeout(() => {
      setIsLoading(false);
    }, 15000);
  }, [searchOptions, sendMessage]);

  // Execute Search Handler (shows Terms Modal First)
  const handleSearch = useCallback((username) => {
    const nick = (username || searchQuery || '').trim();
    if (!nick) {
      showToast(isEn ? 'Please enter a Minecraft username' : 'Пожалуйста, введите никнейм Minecraft');
      return;
    }

    if (!isAuthenticated) {
      showToast(isEn ? 'Please log in to check accounts' : 'Пожалуйста, авторизуйтесь для проверки аккаунтов');
      if (openModal) {
        openModal('login');
      } else if (triggerGoogleLogin) {
        triggerGoogleLogin();
      }
      return;
    }

    if (!isConnected) {
      showToast(isEn ? 'Connecting to backend server...' : 'Подключение к серверу...');
      return;
    }

    if (hasAcceptedTermsRef.current) {
      executeSearch(nick);
      return;
    }

    setPendingTargetUser(nick);
    setIsTermsModalOpen(true);
  }, [isAuthenticated, isConnected, isEn, showToast, openModal, triggerGoogleLogin, searchQuery, executeSearch]);

  // Filter and limit rawResults based on selected options
  const displayResults = useMemo(() => {
    let list = [...rawResults];

    if (searchOptions.database === 'recent') {
      list = list.filter(item => {
        const d = String(item.date || '');
        return d.includes('2026-08') || d.includes('2026-07');
      });
    } else if (searchOptions.database === 'verified') {
      list = list.filter(item => !item.isHash && !isHashValue(item.pass));
      if (!list.length && rawResults.length) {
        list = [rawResults[0]];
      }
    }

    if (searchOptions.maxResults === 'all' || searchOptions.maxResults === 0) {
      return list;
    }
    const limit = Number(searchOptions.maxResults) || 25;
    return list.slice(0, limit);
  }, [rawResults, searchOptions]);

  const handleOptionChange = useCallback((key, val) => {
    setSearchOptions(prev => {
      const nextOptions = { ...prev, [key]: val };

      // Sync the Check Account button loading animation and search results
      const targetUser = (activeTargetUser || searchQuery || '').trim();
      if (targetUser) {
        setIsLoading(true);
        setRawResults([]);
        if (isConnected && isAuthenticated) {
          sendMessage(`${WS_ACCOUNT_CHECKER_TOPICS.APP_ENDPOINT}/start`, {
            username: targetUser,
            options: nextOptions
          });
        }
        setTimeout(() => {
          setIsLoading(false);
        }, 450);
      }

      return nextOptions;
    });

    if (key === 'searchType') {
      showToast(
        isEn
          ? `Search type: ${val === 'exact' ? 'Exact Match' : 'Similar Names'}`
          : `Тип поиска: ${val === 'exact' ? 'Точное совпадение' : 'Похожие имена'}`
      );
    } else if (key === 'maxResults') {
      showToast(
        isEn
          ? `Display limit: ${val === 'all' ? 'All results' : val + ' results'}`
          : `Лимит вывода: ${val === 'all' ? 'Все результаты' : val + ' результатов'}`
      );
    }
  }, [activeTargetUser, searchQuery, isConnected, isAuthenticated, sendMessage, isEn, showToast]);

  const scrollToSettings = useCallback(() => {
    const el = document.getElementById('searchWorkspaceSection');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <PageTransition>
      <div className="checker-page-root min-h-screen text-white relative font-sans">
        <main className="account-checker-page-container">
          {/* 1. ХЕРО БЛОК: STORM CHECK */}
          <AccountCheckerHero />

          {/* 2. ПОЛЕ ВВОДА НИКА + ЧИПЫ НЕДАВНИХ ПОИСКОВ */}
          <AccountSearchHeroBox
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            onSearch={handleSearch}
            isLoading={isLoading}
            onScrollToSettings={scrollToSettings}
            recentSearches={recentSearches}
          />

          {/* 3. ГЛОБАЛЬНАЯ СТАТИСТИКА ИНФРАСТРУКТУРЫ */}
          <DatabaseStats stats={stats} />

          {/* 4. ПАНЕЛЬ АКТИВНОСТИ БОТ-СИСТЕМЫ */}
          <CredentialStuffingStatus stats={botStats} />

          {/* 5. РАБОЧАЯ СЕТКА: НАСТРОЙКА ПОИСКА (390px) + РЕЗУЛЬТАТЫ */}
          <div className="checker-workspace-grid" id="searchWorkspaceSection">
            {/* Левая колонка: Настройка поиска */}
            <SearchOptions
              options={searchOptions}
              onOptionChange={handleOptionChange}
              disabled={isLoading}
            />

            {/* Правая колонка: Результаты поиска аккаунтов */}
            <AccountResults
              results={displayResults}
              totalCount={rawResults.length}
              isLoading={isLoading}
              searchQuery={searchQuery}
              activeTargetUser={activeTargetUser}
              hasSearched={hasSearched}
              onShowToast={showToast}
              onShowAll={() => handleOptionChange('maxResults', 'all')}
            />
          </div>

          {/* 6. АЛГОРИТМ ПРОВЕРКИ */}
          <HowItWorks />
        </main>

        {/* Legal Terms of Service Modal */}
        <TermsOfServiceModal
          isOpen={isTermsModalOpen}
          onClose={() => {
            setIsTermsModalOpen(false);
            setPendingTargetUser(null);
          }}
          onAccept={() => {
            hasAcceptedTermsRef.current = true;
            setIsTermsModalOpen(false);
            if (pendingTargetUser) {
              executeSearch(pendingTargetUser);
            }
            setPendingTargetUser(null);
          }}
          onDecline={() => {
            setIsTermsModalOpen(false);
            setPendingTargetUser(null);
          }}
          t={t}
        />
      </div>
    </PageTransition>
  );
};

export default AccountChecker;